import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../lib/auth";
import { useDepositoEscolhido } from "../../lib/consultas";
import { supabase } from "../../lib/supabase";
import { mensagemDeErro, novaChave, rpc, type RespostaWms } from "../../lib/rpc";
import { localPorCodigo, produtoPorCodigo } from "../../lib/leitura";
import { sinalConcluido, sinalErro, sinalOk } from "../../lib/sinais";
import { fmtNum, nomeTipoLocal } from "../../lib/formato";
import { LeituraCodigo } from "../../componentes/LeituraCodigo";
import { AlertaErro } from "../../componentes/Alerta";
import { Botao, Vazio } from "../../componentes/ui";
import type { DocaPendente, SugestaoPosicao } from "./tipos";

/** Acima disso é quase certo que um código de barras caiu no campo de quantidade. */
const MAX_QTD = 99999;

interface Escolhido { produto_id: string; codigo: string; sku: string | null; nome: string }

/** Endereçamento: tira da doca (ou quarentena) e guarda nas posições, com sugestão de onde guardar. */
export default function Armazenar() {
  const { perfil } = useAuth();
  const [params] = useSearchParams();
  const { deposito } = useDepositoEscolhido(perfil?.deposito_padrao_id);
  const [doca, setDoca] = useState<string | null>(params.get("doca")?.toUpperCase() ?? null);
  const [produto, setProduto] = useState<Escolhido | null>(null);
  const [destino, setDestino] = useState<string | null>(null);
  const [qtd, setQtd] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [lampejo, setLampejo] = useState(0);
  const [tremor, setTremor] = useState(0);
  // a mesma transferência repetida depois de falha de rede usa a mesma chave
  const chave = useRef<string | null>(null);
  const semPosicao = useRef(0);

  const pendentes = useQuery({
    queryKey: ["armazenar", deposito],
    enabled: !!deposito,
    queryFn: async () => {
      const { data, error } = await supabase.from("v_doca_pendente").select("*").eq("deposito_id", deposito!).order("local").order("nome");
      if (error) throw error;
      return data as DocaPendente[];
    },
    refetchInterval: 30_000,
  });

  const docas = useMemo(() => {
    const m = new Map<string, { local: string; tipo: string; produtos: number; unidades: number }>();
    for (const p of pendentes.data ?? []) {
      const d = m.get(p.local) ?? { local: p.local, tipo: p.tipo, produtos: 0, unidades: 0 };
      d.produtos += 1;
      d.unidades += Number(p.quantidade);
      m.set(p.local, d);
    }
    return [...m.values()];
  }, [pendentes.data]);
  const naDoca = (pendentes.data ?? []).filter((p) => p.local === doca);
  const disponivel = produto ? Number(naDoca.find((p) => p.produto_id === produto.produto_id)?.quantidade ?? 0) : 0;

  const sugestoes = useQuery({
    queryKey: ["sugestoes", produto?.produto_id, disponivel, deposito],
    enabled: !!produto && disponivel > 0,
    queryFn: async () => {
      const r = await rpc<RespostaWms & { sugestoes?: SugestaoPosicao[]; sem_posicao?: number }>("wms_sugerir_posicoes", {
        p_codigo_produto: produto!.codigo, p_quantidade: disponivel, p_deposito_id: deposito,
      });
      if (!r.ok) throw new Error(r.mensagem ?? "Sem sugestão");
      semPosicao.current = Number(r.sem_posicao ?? 0);
      return r.sugestoes ?? [];
    },
  });

  // um lugar só com coisa para guardar (o normal): já começa nele
  const escolheuSozinho = useRef(false);
  useEffect(() => {
    if (!doca && !escolheuSozinho.current && docas.length === 1) {
      escolheuSozinho.current = true;
      setDoca(docas[0].local);
    }
  }, [doca, docas]);

  // produto acabou na doca (guardado por outra pessoa ou por esta): volta para a lista
  useEffect(() => {
    if (produto && pendentes.data && disponivel <= 0 && !ocupado) {
      setProduto(null);
      setDestino(null);
    }
  }, [produto, pendentes.data, disponivel, ocupado]);

  function falhar(mensagem: string) {
    sinalErro();
    setTremor((n) => n + 1);
    setErro(mensagem);
  }

  function escolherProduto(p: DocaPendente, codigo?: string) {
    setOk(null);
    setProduto({ produto_id: p.produto_id, codigo: codigo ?? p.sku ?? "", sku: p.sku, nome: p.nome });
    setDestino(null);
  }

  async function leitura(codigo: string) {
    setOk(null);
    if (!doca) {
      const local = await localPorCodigo(codigo);
      if (!local) return falhar("Local não cadastrado neste depósito");
      if (!["RECEBIMENTO", "QUARENTENA"].includes(local.tipo)) {
        return falhar(`${local.codigo} (${nomeTipoLocal[local.tipo] ?? local.tipo}) não é a doca nem a quarentena. Aqui o primeiro passo é de onde tirar; a posição vem no fim. Para mover entre posições, use Mover estoque.`);
      }
      sinalOk();
      setLampejo((n) => n + 1);
      setDoca(local.codigo);
      return;
    }
    if (!produto) {
      const p = await produtoPorCodigo(codigo);
      const item = naDoca.find((i) => i.produto_id === p?.id);
      if (!p || !item) return falhar(`Este produto não está em ${doca}`);
      sinalOk();
      setLampejo((n) => n + 1);
      escolherProduto(item, codigo);
      return;
    }
    const destinoLido = codigo.toUpperCase();
    if (destinoLido === doca) return falhar("O destino precisa ser outra posição");
    const sugestao = sugestoes.data?.find((s) => s.codigo === destinoLido);
    sinalOk();
    setLampejo((n) => n + 1);
    chave.current = novaChave();
    setDestino(destinoLido);
    // plano de alocação primeiro; sem plano, o que cabe; senão tudo o que está na doca
    const cabe = sugestao?.cabe && sugestao.cabe > 0 ? Math.floor(sugestao.cabe) : null;
    const plano = sugestao?.alocar && sugestao.alocar > 0 ? sugestao.alocar : null;
    setQtd(String(plano ? Math.min(disponivel, plano) : cabe ? Math.min(disponivel, cabe) : disponivel));
  }

  const n = Number(qtd.replace(",", "."));
  const qtdValida = Number.isFinite(n) && n > 0 && n <= Math.min(disponivel, MAX_QTD);

  async function guardar() {
    if (!doca || !produto || !destino || !qtdValida) return;
    setOcupado(true);
    try {
      const r = await rpc("wms_transferir", {
        p_codigo_origem: doca, p_codigo_destino: destino, p_codigo_produto: produto.codigo, p_quantidade: n,
        p_idem: chave.current ?? novaChave(), p_deposito_id: deposito,
      });
      chave.current = null;
      if (!r.ok) return falhar(r.mensagem ?? "Transferência recusada");
      sinalConcluido();
      setOk(`${fmtNum(n)} ${produto.nome} guardado(s) em ${destino}`);
      setDestino(null);
      await pendentes.refetch();
      void sugestoes.refetch();
    } catch (e) {
      falhar(`${mensagemDeErro(e)}. Tente de novo: a mesma transferência não é feita duas vezes.`);
    } finally {
      setOcupado(false);
    }
  }

  const rotulo = !doca ? "Toque acima ou bipe de onde vai tirar (doca ou quarentena)" : !produto ? "Bipe o produto a guardar" : "Bipe a posição onde vai guardar";
  const passo = !doca ? 1 : !produto ? 2 : 3;
  const tipoDoca = docas.find((d) => d.local === doca)?.tipo;
  const foraDaSugestao = destino && sugestoes.data && !sugestoes.data.some((s) => s.codigo === destino);

  return (
    <div className="flex flex-col gap-3">
      <AlertaErro mensagem={erro} onFechar={() => setErro(null)} />
      <h1 className="font-sinal text-3xl font-bold">Armazenagem</h1>
      {!doca && (
        <p className="text-aco-escuro">
          Guarde nas posições o que já foi conferido: o que chegou do fornecedor fica na doca (Recebimento) e a devolução em bom estado
          fica na quarentena (Receber devolução). O WMS sugere onde guardar.
        </p>
      )}
      <ol className="grid grid-cols-3 gap-1 text-center text-sm font-semibold" aria-label="Passos">
        {["De onde tirar", "Qual produto", "Onde guardar"].map((t, i) => (
          <li key={t} aria-current={passo === i + 1 ? "step" : undefined}
            className={`rounded-md px-2 py-2 ${passo === i + 1 ? "bg-faixa text-tinta" : passo > i + 1 ? "bg-ok-claro text-ok" : "bg-concreto text-aco-escuro"}`}>
            {i + 1}. {t}
          </li>
        ))}
      </ol>
      {ok && <p role="status" className="rounded-md bg-ok-claro px-3 py-2 font-semibold text-ok">{ok}</p>}

      {!doca && (
        docas.length ? (
          <ul className="flex flex-col gap-2">
            {docas.map((d) => (
              <li key={d.local}>
                <button type="button" onClick={() => { setDoca(d.local); setOk(null); }}
                  className="flex min-h-16 w-full items-center justify-between rounded-lg border-2 border-tinta bg-chapa px-4 py-3 text-left">
                  <span>
                    <span className="block text-sm text-aco-escuro">{nomeTipoLocal[d.tipo] ?? d.tipo}</span>
                    <span className="font-sinal text-3xl font-bold">{d.local}</span>
                  </span>
                  <span className="text-right font-semibold">{d.produtos} produto(s)<span className="block">{fmtNum(d.unidades)} un</span></span>
                </button>
              </li>
            ))}
          </ul>
        ) : <Vazio>{pendentes.isLoading ? "Carregando..." : "Nada para guardar: a doca e a quarentena estão vazias."}</Vazio>
      )}

      {doca && (
        <section className="rounded-lg border-2 border-tinta bg-chapa">
          <div className="flex items-center justify-between border-b-2 border-aco px-4 py-3">
            <span>
              <span className="text-sm text-aco-escuro">{tipoDoca === "QUARENTENA" ? "Tirando da quarentena" : tipoDoca === "RECEBIMENTO" ? "Tirando da doca" : "Tirando de"}</span>
              <span className="block font-sinal text-4xl font-bold">{doca}</span>
            </span>
            <Botao variante="fantasma" onClick={() => { setDoca(null); setProduto(null); setDestino(null); }}>Trocar</Botao>
          </div>
          {naDoca.length ? (
            <ul className="divide-y divide-aco">
              {naDoca.map((i) => (
                <li key={i.produto_id}>
                  <button type="button" disabled={!!destino} onClick={() => escolherProduto(i)}
                    className={`flex min-h-12 w-full items-center justify-between gap-3 px-4 py-2 text-left ${produto?.produto_id === i.produto_id ? "bg-faixa-clara" : ""}`}>
                    <span><span className="font-semibold">{i.sku}</span> <span className="text-aco-escuro">{i.nome}</span></span>
                    <strong className="font-sinal text-xl">{fmtNum(i.quantidade)}</strong>
                  </button>
                </li>
              ))}
            </ul>
          ) : <div className="p-4"><Vazio>{pendentes.isLoading ? "Carregando..." : `Nada a guardar em ${doca}.`}</Vazio></div>}
        </section>
      )}

      {produto && !destino && (
        <section className="rounded-lg border-2 border-aco bg-chapa p-4">
          <p className="font-semibold">{produto.sku} {produto.nome}: {fmtNum(disponivel)} un para guardar</p>
          <h2 className="mt-2 font-sinal text-xl font-semibold">Onde guardar</h2>
          {sugestoes.isLoading ? <p className="text-aco-escuro">Buscando posições...</p> : sugestoes.data?.length ? (
            <ul className="mt-2 flex flex-col gap-2">
              {sugestoes.data.map((s) => (
                <li key={s.codigo} className="flex items-center justify-between gap-3 rounded-md border-2 border-aco px-3 py-2">
                  <span>
                    <span className="block font-sinal text-3xl font-bold leading-tight">{s.codigo}</span>
                    <span className="text-sm text-aco-escuro">{nomeTipoLocal[s.tipo] ?? s.tipo} · {s.motivo}</span>
                  </span>
                  <span className="text-right text-sm">
                    {s.alocar !== undefined && s.alocar > 0 && (
                      <span className="mb-1 block rounded bg-faixa px-2 py-1 font-sinal text-xl font-bold text-tinta">guarde {fmtNum(s.alocar)}</span>
                    )}
                    {s.cabe !== null && s.cabe !== undefined ? <span className="block font-semibold">cabem {fmtNum(s.cabe)}</span> : <span className="block">sem limite cadastrado</span>}
                    {Number(s.atual) > 0 && <span className="block text-aco-escuro">tem {fmtNum(s.atual)}</span>}
                  </span>
                </li>
              ))}
            </ul>
          ) : <p className="text-aco-escuro">{sugestoes.error ? mensagemDeErro(sugestoes.error) : "Sem sugestão: guarde em qualquer posição de armazenagem livre."}</p>}
          {!!sugestoes.data?.length && semPosicao.current > 0 && (
            <p className="mt-2 font-semibold text-alerta">{fmtNum(semPosicao.current)} un não cabem nas posições sugeridas: guarde em outra posição livre.</p>
          )}
        </section>
      )}

      {produto && destino ? (
        <section data-dialogo-aberto className="flex flex-col gap-3 rounded-lg bg-faixa p-4">
          <p className="text-lg font-semibold">{produto.nome}: {doca} para <span className="font-sinal text-2xl">{destino}</span></p>
          {foraDaSugestao && <p className="font-semibold">Posição fora das sugestões. Confira antes de guardar.</p>}
          <form className="flex items-center gap-3" onSubmit={(e) => { e.preventDefault(); void guardar(); }}>
            <button type="button" onClick={() => setQtd(String(Math.max(1, (qtdValida ? n : 1) - 1)))}
              className="h-14 w-14 shrink-0 rounded-md border-2 border-tinta bg-chapa text-2xl font-bold" aria-label="Menos um">-</button>
            <input autoFocus inputMode="decimal" value={qtd} onChange={(e) => setQtd(e.target.value.replace(/[^\d.,]/g, ""))} onFocus={(e) => e.target.select()}
              className="h-14 w-full rounded-md border-2 border-tinta bg-chapa text-center font-sinal text-3xl font-bold" aria-label="Quantidade a guardar" />
            <button type="button" onClick={() => setQtd(String(Math.min(disponivel, (qtdValida ? n : 0) + 1)))}
              className="h-14 w-14 shrink-0 rounded-md border-2 border-tinta bg-chapa text-2xl font-bold" aria-label="Mais um">+</button>
          </form>
          <p className="text-sm">Na doca: {fmtNum(disponivel)} un</p>
          <div className="flex gap-2">
            <Botao variante="secundario" onClick={() => setDestino(null)}>Voltar</Botao>
            <Botao variante="confirmar" grande className="flex-1" disabled={ocupado || !qtdValida} onClick={() => void guardar()}>
              Guardar {qtdValida ? fmtNum(n) : ""}
            </Botao>
          </div>
        </section>
      ) : (
        (doca || docas.length > 0) && (
          <LeituraCodigo rotulo={rotulo} onLeitura={(c) => void leitura(c)} desabilitado={ocupado} lampejo={lampejo} erro={tremor} />
        )
      )}
      <Link to="/recebimento" className="text-center font-semibold underline">Voltar ao recebimento</Link>
    </div>
  );
}
